import clsx from 'clsx'
import { trackEvent } from 'helpers/analytics'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '../../../../components/tailwind/Button'
import { Heading } from '../../../../components/ui/Heading'

export default function NameInputMessage({ onSubmit, onSkip, className }) {
  const { t } = useTranslation()
  const [firstname, setFirstname] = useState('')
  const [lastname, setLastname] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleSubmit = async () => {
    if (firstname.length === 0 && lastname.length === 0) return

    setIsLoading(true)
    try {
      trackEvent('discover_name_submitted')
      await onSubmit({ firstname: firstname.trim(), lastname: lastname.trim() })
      setIsSubmitted(true)
    } catch (error) {
      console.error('Error submitting name:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSkip = () => {
    trackEvent('discover_name_skipped')
    setIsSubmitted(true)
    onSkip()
  }

  const isFormValid = firstname.length > 0 || lastname.length > 0

  return (
    <div
      className={clsx(
        'w-full rounded-lg border border-lightGrey bg-paleWhiteBlue/40 p-4',
        className
      )}
    >
      <Heading level={5}>{t('discover.assistant.name_input.title')}</Heading>

      <p className='mt-1 text-sm font-normal text-darkBlack'>
        {t('discover.assistant.name_input.description')}
      </p>

      {!isSubmitted && (
        <div className='mt-4 space-y-4'>
          {/* Firstname Input */}
          <div>
            <label
              htmlFor='firstname'
              className='mb-1 block text-sm font-medium text-gray-700'
            >
              {t('discover.assistant.name_input.firstname_label')}
            </label>
            <input
              id='firstname'
              type='text'
              value={firstname}
              onChange={(e) => setFirstname(e.target.value)}
              placeholder={t(
                'discover.assistant.name_input.firstname_placeholder'
              )}
              className='w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue'
              disabled={isLoading}
            />
          </div>

          {/* Lastname Input */}
          <div>
            <label
              htmlFor='lastname'
              className='mb-1 block text-sm font-medium text-gray-700'
            >
              {t('discover.assistant.name_input.lastname_label')}
            </label>
            <input
              id='lastname'
              type='text'
              value={lastname}
              onChange={(e) => setLastname(e.target.value)}
              placeholder={t(
                'discover.assistant.name_input.lastname_placeholder'
              )}
              className='w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primaryBlue focus:outline-none focus:ring-1 focus:ring-primaryBlue'
              disabled={isLoading}
            />
          </div>

          {/* Action Buttons */}
          <div className='mt-6 flex flex-col gap-2'>
            <Button.PrimaryBase
              text={t('discover.assistant.name_input.submit_button')}
              onClick={handleSubmit}
              disabled={!isFormValid || isLoading}
              className='flex-1'
            />
            <Button.SecondaryBase
              text={t('discover.assistant.name_input.skip_button')}
              onClick={handleSkip}
              disabled={isLoading}
              className='flex-1'
            />
            <p className='mt-2 text-center text-xs text-gray-500'>
              {t(
                'discover.assistant.name_input.settings_hint',
                'Du kannst das jederzeit in den Einstellungen ändern.'
              )}
            </p>
          </div>
        </div>
      )}

      {isSubmitted && (
        <div className='mt-4 rounded-lg bg-green-50 p-3'>
          <p className='text-sm text-green-700'>
            {firstname && lastname
              ? t('discover.assistant.name_input.success_message', {
                  firstname,
                  lastname
                })
              : t('discover.assistant.name_input.skipped_message')}
          </p>
        </div>
      )}
    </div>
  )
}
